import ErrorHandler from '../utils/ErrorHandler';
import { API_CONFIG } from '../config/api';
import clientAuthService from './ClientAuthService';

/**
 * Client Data Service - Handles client-specific API calls
 * Uses client authentication token instead of admin token
 */
class ClientDataService {
  /**
   * Make authenticated API request with client token
   */
  async makeClientRequest(url, options = {}) {
    // Get client token
    const clientToken = clientAuthService.getAuthToken();
    
    if (!clientToken) {
      throw new Error('Client authentication required');
    }

    // Create headers with client token
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${clientToken}`,
      ...options.headers
    };

    // Build full URL
    const fullUrl = url.startsWith('http') ? url : `${API_CONFIG.BASE_URL}${url}`;

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

    try {
      const response = await fetch(fullUrl, {
        ...options,
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Handle response
      if (response.status === 204) {
        return { success: true, message: 'Operation completed successfully' };
      }

      let data;
      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');
      
      if ((contentLength === '0' || !contentType) && response.ok) {
        return { success: true, message: 'Operation completed successfully' };
      }
      
      let responseText = '';
      try {
        responseText = await response.text();
      } catch (textError) {
        if (response.ok) {
          return { success: true, message: 'Operation completed successfully' };
        }
        responseText = JSON.stringify({ message: 'Failed to read response' });
      }
      
      if (contentType && contentType.includes('application/json')) {
        try {
          data = responseText ? JSON.parse(responseText) : null;
        } catch (parseError) {
          if (response.ok) {
            return { success: true, message: 'Operation completed successfully' };
          }
          data = { message: responseText || 'Failed to parse response' };
        }
      } else {
        data = responseText || (response.ok ? { success: true, message: 'Operation completed successfully' } : { message: 'Request failed' });
      }

      if (!response.ok) {
        const error = new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        error.data = data;
        
        // Handle 401 - client token expired
        if (response.status === 401) {
          clientAuthService.logout();
          throw new Error('Session expired. Please login again.');
        }
        
        throw error;
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timed out');
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        const networkError = new Error('Network error - unable to connect to server');
        networkError.name = 'NetworkError';
        throw networkError;
      }
      
      throw error;
    }
  }

  /**
   * Get all projects for the authenticated client
   */
  async getClientProjects() {
    try {
      const clientToken = clientAuthService.getAuthToken();
      if (!clientToken) {
        throw new Error('Client authentication required');
      }

      return await this.makeClientRequest(API_CONFIG.ENDPOINTS.CLIENT_PROJECTS, {
        method: 'GET'
      });
    } catch (error) {
      throw ErrorHandler.handleApiError(error, 'Get Client Projects');
    }
  }

  /**
   * Get specific project for the authenticated client
   */
  async getClientProject(projectId) {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    try {
      const clientToken = clientAuthService.getAuthToken();
      if (!clientToken) {
        throw new Error('Client authentication required');
      }

      return await this.makeClientRequest(API_CONFIG.ENDPOINTS.CLIENT_PROJECT_BY_ID(projectId), {
        method: 'GET'
      });
    } catch (error) {
      throw ErrorHandler.handleApiError(error, `Get Client Project ${projectId}`);
    }
  }
}

// Export singleton instance
const clientDataService = new ClientDataService();
export default clientDataService;

